import { useInfiniteQuery } from "@tanstack/react-query";
import clsx from "clsx";

import { api } from "@/api";
import {
  Button,
  EmptyState,
  ErrorState,
  money,
  Panel,
  Skeleton,
} from "@/components/ui";

import { ledgerEntryLabel, type LedgerResponse } from "./types";

const STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  authorized: "Processing",
  completed: "Completed",
  failed: "Failed",
  voided: "Voided",
  refunded: "Refunded",
};

/** Purpose-labelled money history straight from the four-axis ledger. */
export function ActivityHistory({ playerId }: { playerId: number }) {
  const activity = useInfiniteQuery({
    queryKey: ["ledger", playerId],
    queryFn: ({ pageParam }) =>
      api.myLedger(pageParam) as Promise<LedgerResponse>,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: 30_000,
  });

  if (activity.isPending) return <Skeleton rows={8} variant="table" />;
  if (activity.error) {
    return (
      <ErrorState
        error={activity.error}
        retry={() => void activity.refetch()}
      />
    );
  }
  const entries = activity.data.pages.flatMap((page) => page.entries);
  return (
    <Panel className="history-table activity-history">
      {entries.length ? (
        <>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Activity</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.entryId}>
                  <td>{new Date(entry.createdAt).toLocaleString()}</td>
                  <td>{ledgerEntryLabel(entry)}</td>
                  <td
                    className={clsx(
                      "activity-amount",
                      entry.direction === "credit"
                        ? "activity-amount--credit"
                        : "activity-amount--debit",
                    )}
                  >
                    {entry.direction === "credit" ? "+" : "−"}
                    {money(entry.amountCents, entry.currency)}
                  </td>
                  <td>
                    <span
                      className={`history-result history-result--${entry.status}`}
                    >
                      {STATUS_LABELS[entry.status] ?? entry.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {activity.hasNextPage ? (
            <footer>
              <Button
                disabled={activity.isFetchingNextPage}
                onClick={() => void activity.fetchNextPage()}
                variant="secondary"
              >
                {activity.isFetchingNextPage ? "Loading…" : "Load more"}
              </Button>
            </footer>
          ) : null}
        </>
      ) : (
        <EmptyState
          detail="Pack purchases, sales, purse payouts, prizes and bets will appear here."
          title="No transactions yet"
        />
      )}
    </Panel>
  );
}
